// Contractele pure ale workspace-ului `Protecția datelor`. Query-ul rutei este
// singura sursă de adevăr pentru secțiune, vedere, filtre și pagină, astfel încât
// deep-linkul și Back să restaureze exact contextul (`sprint-4-implementation-handoff §5.1`).

// Numai secțiunile deja livrate. Informările și auditul apar când sprinturile
// lor sunt implementate, nu ca ecrane goale.
export const WORKSPACE_SECTIONS = Object.freeze(["dashboard", "registry", "purposes", "notices"]);
// Secțiunile ale căror API-uri sunt numai pentru managerul Privacy: celelalte
// roluri nu văd tab-ul, în loc să primească un ecran care răspunde 403.
export const MANAGER_SECTIONS = Object.freeze(["notices"]);
export const REGISTRY_VIEWS = Object.freeze(["persons", "companies", "contact_points", "events", "evidence"]);
// Dashboard-ul are numai vederile de record; celelalte aparțin registrului.
export const SUBJECT_VIEWS = Object.freeze(["persons", "companies"]);
export const SUMMARY_STATES = Object.freeze([
  "active",
  "partial",
  "pending",
  "withdrawn_or_refused",
  "other_basis",
  "undocumented",
  "not_applicable",
]);
export const REGISTRY_PAGE_SIZE = 25;
export const CHANNELS = Object.freeze(["email", "phone", "sms", "whatsapp", "telegram", "postal"]);
export const CLASSIFICATIONS = Object.freeze(["corporate_generic", "personal_named", "linked_to_person", "unknown"]);
// S5.4: legătura cu un record CRM; `unlinked_widget` = puncte provizorii din formulare publice.
export const CONTACT_POINT_LINKS = Object.freeze(["linked", "unlinked", "unlinked_widget"]);
export const PAIR_STATES = Object.freeze([
  "granted", "pending_confirmation", "refused", "withdrawn", "objected", "restricted", "expired", "other_basis",
]);
export const EVENT_TYPES = Object.freeze([
  "granted", "pending_confirmation", "confirmed", "refused", "withdrawn",
  "objected", "restricted", "expired", "corrected", "informed",
]);
export const SOURCE_TYPES = Object.freeze([
  "widget", "preference_center", "paper", "api", "mailjet", "operator", "import", "system",
]);
export const EVIDENCE_TYPES = Object.freeze([
  "web_form", "paper_scan", "api_assertion", "mailjet_event", "operator_statement", "legacy_delivery",
  "information_notice",
]);
// „Cu / fără" — pentru dovada unui eveniment și pentru atașamentul unei dovezi.
export const EVIDENCE_FILTERS = Object.freeze(["with", "without"]);
export const PERIOD_DAYS = Object.freeze(["7", "30", "90", "365"]);
export const MAILJET_SYNC_FILTERS = Object.freeze(["failed"]);
export const SPAM_FILTERS = Object.freeze(["to_review"]);

// Filtrele fiecărei vederi, în ordinea din UI. Numai acestea ajung în URL și în
// request, deci schimbarea vederii renunță la filtrele care nu i se aplică.
export const VIEW_FILTERS = Object.freeze({
  persons: ["summaryState", "search"],
  companies: ["summaryState", "search"],
  contact_points: ["channel", "classification", "link", "purposeId", "pairState", "mailjetSync", "spam", "search"],
  events: ["eventType", "sourceType", "purposeId", "channel", "evidence", "period"],
  evidence: ["evidenceType", "attachment", "period"],
});

// Cheia din URL, cheia din API și valorile permise pentru fiecare filtru.
const FILTERS = Object.freeze({
  summaryState: { query: "summary_state", api: "summary_state", allowed: SUMMARY_STATES },
  search: { query: "search", api: "search" },
  channel: { query: "channel", api: "channel_code", allowed: CHANNELS },
  classification: { query: "classification", api: "classification", allowed: CLASSIFICATIONS },
  link: { query: "link", api: "link", allowed: CONTACT_POINT_LINKS },
  purposeId: { query: "purpose", api: "purpose_id" },
  pairState: { query: "state", api: "state", allowed: PAIR_STATES },
  eventType: { query: "event_type", api: "event_type", allowed: EVENT_TYPES },
  sourceType: { query: "source", api: "source_type", allowed: SOURCE_TYPES },
  evidence: { query: "evidence", api: "has_evidence", allowed: EVIDENCE_FILTERS },
  evidenceType: { query: "evidence_type", api: "evidence_type", allowed: EVIDENCE_TYPES },
  attachment: { query: "attachment", api: "has_attachment", allowed: EVIDENCE_FILTERS },
  period: { query: "period", api: "from", allowed: PERIOD_DAYS },
  mailjetSync: { query: "mailjet_sync", api: "mailjet_sync", allowed: MAILJET_SYNC_FILTERS }, // S8.5b: ultimul job eșuat
  spam: { query: "spam", api: "spam", allowed: SPAM_FILTERS }, // S8.5c: plângere spam de verificat
});
const DAY_MS = 24 * 60 * 60 * 1000;

const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

const positivePage = (value) => {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const parseFilter = (key, value) => {
  if (key === "search") return String(value ?? "").trim().slice(0, 120);
  if (key === "purposeId") {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }
  return pick(value, FILTERS[key].allowed, null);
};

// Orice valoare necunoscută din URL (link vechi, editat manual) cade pe default,
// nu ajunge la backend ca filtru invalid.
export const parseWorkspaceQuery = (query = {}) => ({
  section: pick(query.section, WORKSPACE_SECTIONS, "dashboard"),
  view: pick(query.view, REGISTRY_VIEWS, "persons"),
  ...Object.fromEntries(Object.entries(FILTERS).map(([key, { query: name }]) => [key, parseFilter(key, query[name])])),
  page: positivePage(query.page),
});

const activeFilters = (state) => (VIEW_FILTERS[state.view] || [])
  .filter((key) => state[key] !== null && state[key] !== undefined && state[key] !== "");

// Păstrează numai cheile cu valoare ale vederii curente, ca URL-ul să rămână
// scurt, stabil și fără filtre care nu se aplică.
export const buildWorkspaceQuery = (state) => {
  const query = { section: state.section, view: state.view };
  for (const key of activeFilters(state)) query[FILTERS[key].query] = String(state[key]);
  if (state.page > 1) query.page = String(state.page);
  return query;
};

export const hasActiveFilters = (state) => activeFilters(state).length > 0;

// KPI-urile de nivel record deschid Registrul deja filtrat, pe vederea selectată
// în dashboard, deci numărul din KPI și totalul din registru sunt aceleași.
// Pagina și căutarea se resetează: un filtru nou nu moștenește rezultatele vechi.
export const DRILL_DOWN_KPIS = Object.freeze({
  evaluated: null,
  withdrawn_or_refused: "withdrawn_or_refused",
  partial: "partial",
  undocumented: "undocumented",
});

export const drillDownState = (state, summaryState) => ({
  ...state,
  section: "registry",
  summaryState: summaryState || null,
  search: "",
  page: 1,
});

// S8.5b: KPI-ul „Propagări Mailjet eșuate” deschide Puncte de contact numai cu filtrul lui (celelalte filtre ale
// vederii se șterg), ca totalul registrului să fie numărul din KPI.
export const failedSyncDrillDownState = (state) => ({
  ...state,
  ...Object.fromEntries(VIEW_FILTERS.contact_points.map((key) => [key, null])),
  section: "registry", view: "contact_points", mailjetSync: "failed", search: "", page: 1,
});

const apiValue = (key, value, now) => {
  if (key === "evidence" || key === "attachment") return value === "with" ? "true" : "false";
  if (key === "period") return new Date(now - Number(value) * DAY_MS).toISOString();
  return value;
};

export const registryRequestParams = (state, now = Date.now()) => ({
  view: state.view,
  ...Object.fromEntries(activeFilters(state).map((key) => [FILTERS[key].api, apiValue(key, state[key], now)])),
  limit: REGISTRY_PAGE_SIZE,
  offset: (state.page - 1) * REGISTRY_PAGE_SIZE,
});

// Recordul se deschide direct pe tabul `Protecția datelor`; `subjectType` este
// tipul din API (`person|company`) sau vederea (`persons|companies`).
export const recordRoute = (subjectType, id) => ({
  path: `/ncrm/${["companies", "company"].includes(subjectType) ? "companies" : "people"}/${id}`,
  query: { tab: "data-protection" },
});

// `03.10.2026, 09:34`; o valoare lipsă sau invalidă devine „—”.
export const formatRegistryDateTime = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "—";
  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
